import { useCallback, useEffect, useMemo } from 'react';

import { useNotices } from '@/features/notices/useNotices';
import { ApiCreateDocumentDtoV2, ApiDocumentResponseV2 } from '@/shared/api';
import { useApi } from '@/shared/api/hooks/useApi';

import { useCasesStore } from '../stores/cases.store';
import { useDocumentsStore } from '../stores/documents.store';

import { useCases } from './useCases';

export const useDocuments = () => {
  const api = useApi();
  const notices = useNotices();
  const { attachDocument, fetchCases } = useCases();
  const { cases, isInitialized: isCasesInitializedGlobal, updateCase } = useCasesStore();
  const {
    documents,
    isLoading,
    setLoading,
    setInitialized,
    setDocuments,
    updateDocument,
    addDocument: addDocumentStore,
    removeDocument: removeDocumentFromStore,
  } = useDocumentsStore();

  const fetchDocuments = useCallback(async () => {
    setLoading(true);

    const response = await api.documentsGetAll();

    if (response.error) {
      notices.error('Failed to load documents');
      setLoading(false);
      setInitialized(true);
      return [];
    }

    setDocuments(response.data ?? []);
    setLoading(false);
    setInitialized(true);
    return [];
  }, [api, notices, setDocuments, setLoading, setInitialized]);

  const deleteDocument = useCallback(
    async (id: string): Promise<boolean> => {
      const response = await api.documentsDelete(id);

      if (response.error) {
        notices.error('Failed to delete document');
        return false;
      }

      const documentToRemove = documents.find((d) => d.id === id);
      if (documentToRemove) {
        removeDocumentFromStore(documentToRemove.id);
      } else {
        fetchDocuments();
      }
      fetchCases();

      return true;
    },
    [api, notices, documents, removeDocumentFromStore, fetchDocuments, fetchCases],
  );

  const getDocumentsByCaseId = useCallback(
    (id: string): ApiDocumentResponseV2[] => {
      const caseDocuments = cases.find((c) => c.id === id)?.caseDocuments;
      return documents.filter(
        (item) => caseDocuments?.some((caseDocument) => caseDocument.documentId === item.id),
      );
    },
    [cases, documents],
  );

  const addDocument = useCallback(
    async (data: ApiCreateDocumentDtoV2) => {
      const res = await api.documentsCreate(data);
      if (res.error) {
        notices.error('Failed to add document');
        return null;
      }
      if (res.data) {
        addDocumentStore(res.data);
        if (data.caseIds && data.caseIds[0]) {
          const { cases: liveCases } = useCasesStore.getState();
          const caseData = liveCases.find((c) => c.id === data.caseIds?.[0]);
          const caseDocument = res.data.caseDocuments.find((c) => c.caseId === data.caseIds?.[0]);
          if (caseDocument) {
            updateCase(data.caseIds[0], {
              caseDocuments: [...(caseData?.caseDocuments ?? []), caseDocument],
            });
          }
        }
      }
      return res.data;
    },
    [api, notices, cases, attachDocument, addDocumentStore],
  );

  const downloadDocument = useCallback(
    async (filePath: string) => {
      const document = documents.find((d) => d.filePath === filePath);
      if (!document) {
        notices.error('Failed to download document');
        return null;
      }
      const response = await api.documentsDownload({
        filePath: document.filePath,
      });
      if (response.error) {
        notices.error('Failed to download document');
        return null;
      }
      api.documentsMarkAsRead(document.id);
      updateDocument(document.id, { isNew: false });
      return response.data;
    },
    [api, notices, documents, updateDocument],
  );

  useEffect(() => {
    // get the live store state to avoid race conditions
    const { isInitialized, isLoading: isLoadingLive } = useDocumentsStore.getState();
    const { isInitialized: isCasesInitialized } = useCasesStore.getState();
    if (!isInitialized && !isLoadingLive && isCasesInitialized) {
      fetchDocuments();
    }
  }, [fetchDocuments, isCasesInitializedGlobal]);

  return useMemo(
    () => ({
      documents,
      isLoading,
      addDocument,
      getDocumentsByCaseId,
      fetchDocuments,
      deleteDocument,
      downloadDocument,
    }),
    [
      documents,
      isLoading,
      addDocument,
      getDocumentsByCaseId,
      fetchDocuments,
      deleteDocument,
      downloadDocument,
    ],
  );
};
